// src/components/media360/hooks/useMedia360Controller.js
// Hook escalable: mantiene compatibilidad (items, doUpload, etc.) + añade experiencias agrupadas.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../../hooks";
import { Video360Api } from "../../../../api/revi/Video360Api";

import { stripExt } from "../domain/fileTypes";
import { groupItemsToExperiences } from "../domain/groupExperiences";
import { buildPlayerUrlFromExperience } from "../domain/playerUrl";

/**
 * Controller de Media360:
 * - No toca backend.
 * - Agrupa por convención de nombres (media + __audio).
 * - Loop se deduce por "__loop" en el nombre (inyectado en title).
 */
export function useMedia360Controller() {
  const { accessToken } = useAuth();
  const api = useMemo(() => new Video360Api(), []);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadProgressKnown, setUploadProgressKnown] = useState(false);

  const [items, setItems] = useState([]);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [error, setError] = useState(null);

  const experiences = useMemo(() => groupItemsToExperiences(items), [items]);

  const reload = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.list(accessToken, { pageToken: null });
      setItems(Array.isArray(res?.items) ? res.items : []);
      setNextPageToken(res?.nextPageToken || null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [accessToken, api]);

  const loadMore = useCallback(async () => {
    if (!accessToken || !nextPageToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.list(accessToken, { pageToken: nextPageToken });
      const more = Array.isArray(res?.items) ? res.items : [];
      setItems((prev) => [...prev, ...more]);
      setNextPageToken(res?.nextPageToken || null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [accessToken, api, nextPageToken]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /**
   * Upload robusto de 1 experiencia:
   * - Sube media principal (video o image)
   * - Si hay audio, lo sube después con title basado en el nombre real devuelto por GCS
   *
   * @param {Object} p
   * @param {File} p.mediaFile
   * @param {"video"|"image"} p.mediaKind
   * @param {string} p.title
   * @param {"360"|"TB"|"LR"} p.stereoType
   * @param {boolean} p.loop
   * @param {File=} p.audioFile
   */
  const doUploadExperience = useCallback(
    async ({ mediaFile, mediaKind, title, stereoType, loop, audioFile }) => {
      if (!accessToken) throw new Error("Missing accessToken");
      if (!mediaFile) throw new Error("Missing mediaFile");
      if (!title) throw new Error("Missing title");

      setUploading(true);
      setUploadProgress(0);
      setUploadProgressKnown(false);
      setError(null);

      // Inyectamos __loop en el title (convención sin backend)
      const finalTitle = loop ? `${title}__loop` : title;

      try {
        // 1) Subida media principal (0%..70%)
        const mediaRes = await api.upload(accessToken, {
          file: mediaFile,
          title: finalTitle,
          type: stereoType, // 360|TB|LR (tu backend ya lo entiende)
          onProgress: (pct, known) => {
            setUploadProgressKnown(Boolean(known));
            const clamped = Math.max(0, Math.min(100, Number(pct) || 0));
            setUploadProgress(Math.round(clamped * 0.7));
          },
        });

        // 2) Subida audio adicional opcional (70%..100%)
        let audioRes = null;
        if (audioFile) {
          const baseKey = stripExt(mediaRes?.name || "");
          // Convención: el audio termina en __audio, y type fijo "360" para no duplicar TB/LR
          const audioTitle = `${baseKey}__audio`;

          audioRes = await api.upload(accessToken, {
            file: audioFile,
            title: audioTitle,
            type: "360",
            onProgress: (pct, known) => {
              setUploadProgressKnown(Boolean(known));
              const clamped = Math.max(0, Math.min(100, Number(pct) || 0));
              setUploadProgress(70 + Math.round(clamped * 0.3));
            },
          });
        }

        // Refrescamos listado
        await reload();

        // Devolvemos un objeto “experience” útil para mostrar URLs al momento
        const exp = {
          groupKey: stripExt(mediaRes?.name || ""),
          mediaKind: mediaKind || "video",
          mode: api.inferModeFromName(mediaRes?.name || ""),
          loop: Boolean(loop),
          media: mediaRes,
          audio: audioRes,
        };

        return {
          media: mediaRes,
          audio: audioRes,
          experience: exp,
          playerUrl: buildPlayerUrlFromExperience(exp, {
            forceAudioLoop: true,
          }),
        };
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setUploading(false);
      }
    },
    [accessToken, api, reload],
  );

  /**
   * Compatibilidad: tu UI actual llama doUpload({file,title,type})
   * - Por defecto lo tratamos como vídeo.
   * - Loop/audio vendrán en el nuevo formulario (fase siguiente).
   */
  const doUpload = useCallback(
    async ({ file, title, type }) => {
      return doUploadExperience({
        mediaFile: file,
        mediaKind: "video",
        title,
        stereoType: type || "360",
        loop: false,
        audioFile: undefined,
      });
    },
    [doUploadExperience],
  );

  /**
   * Borra una experiencia completa (media + audio) por groupKey.
   * El groupKey aquí coincide con stripExt(media.name).
   */
  const doDeleteExperience = useCallback(
    async (groupKey) => {
      if (!accessToken) throw new Error("Missing accessToken");
      if (!groupKey) return;

      setLoading(true);
      setError(null);

      try {
        const exp = experiences.find((e) => e.groupKey === groupKey);
        if (!exp) return;

        // Borra audio primero si existe
        if (exp.audio?.name) {
          await api.remove(accessToken, exp.audio.name);
        }
        // Borra media principal
        if (exp.media?.name) {
          await api.remove(accessToken, exp.media.name);
        }

        await reload();
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [accessToken, api, experiences, reload],
  );

  /**
   * Compatibilidad: borrar un objeto suelto por name (UI actual).
   */
  const doDelete = useCallback(
    async (name) => {
      if (!accessToken) throw new Error("Missing accessToken");
      if (!name) return;

      setLoading(true);
      setError(null);
      try {
        await api.remove(accessToken, name);
        await reload();
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [accessToken, api, reload],
  );

  /**
   * Renombrado (mantiene comportamiento actual).
   * Nota: si renombraras media, romperías emparejamiento con audio si no renombras ambos.
   * En la UI nueva lo trataremos como “rename experience” (fase siguiente).
   */
  const doRename = useCallback(
    async ({ name, newName }) => {
      if (!accessToken) throw new Error("Missing accessToken");
      if (!name || !newName) return;

      setLoading(true);
      setError(null);
      try {
        await api.rename(accessToken, { name, newName });
        await reload();
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [accessToken, api, reload],
  );

  return {
    state: {
      loading,
      uploading,
      uploadProgress,
      uploadProgressKnown,

      // Compat actual
      items,
      nextPageToken,
      error,

      // Nuevo: agrupado por experiencia
      experiences,
    },
    actions: {
      reload,
      loadMore,

      // Nuevo (subida completa)
      doUploadExperience,
      doDeleteExperience,

      // Compat con lo existente
      doUpload,
      doDelete,
      doRename,

      // Helpers
      inferModeFromName: api.inferModeFromName.bind(api),
      buildPlayerUrl: api.buildPlayerUrl.bind(api),
      buildPlayerUrlFromExperience: (exp) =>
        buildPlayerUrlFromExperience(exp, { forceAudioLoop: true }),
    },
  };
}
